import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {ResizeObserver | null} */
  #summaryObserver = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#onDialogOpen);
    // Native <details> toggle (discount/note) — unlock sticky before ResizeObserver paints.
    this.addEventListener('toggle', this.#onDetailsToggle, true);
    // Discount/note disclosure grows summary — remeasure sticky or iOS Safari clips the field.
    this.#summaryObserver = new ResizeObserver(() => this.#updateStickyState());
    queueMicrotask(() => this.#observeSummary());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#onDialogOpen);
    this.removeEventListener('toggle', this.#onDetailsToggle, true);
    this.#summaryObserver?.disconnect();
    this.#summaryObserver = null;
  }

  #onDetailsToggle = (event) => {
    if (!(event.target instanceof HTMLDetailsElement)) return;
    this.#updateStickyState();
  };

  #onDialogOpen = () => {
    this.#observeSummary();
    this.#updateStickyState();
  };

  #observeSummary() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    const summary = dialog?.querySelector('.cart-drawer__summary');
    if (!summary || !this.#summaryObserver) return;
    this.#summaryObserver.disconnect();
    this.#summaryObserver.observe(summary);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    // Open discount/note panels need room — sticky+mask clips them on iOS Safari.
    const panelOpen = summary.querySelector(
      '.disclosure-trigger[aria-expanded="true"], details[open], .disclosure-content:not([inert])'
    );
    if (panelOpen) {
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
